import React from 'react';
import { ArcDroneMarker } from '@src/core/controllers';
import { getFlightHeading } from '@src/features/order/provider/flight-provider/flight-position-cache';
import { DroneEntity } from '@src/core/features';
import { useFlightProvider } from '@src/features/order/provider';
import { isDroneSignalLost } from '@src/features/drone/utils';
import { DroneMarkersListProps } from './types';

interface IdleRowProps {
  drone: DroneEntity;
  isSelected: boolean;
  lost: boolean;
  onClick: (drone: DroneEntity) => void;
}

const IdleDroneRow = React.memo<IdleRowProps>(({ drone, isSelected, lost, onClick }) => {
  const position = React.useMemo(
    () => ({ lat: Number(drone.latitude), lng: Number(drone.longitude) }),
    [drone.latitude, drone.longitude]
  );
  const handleClick = React.useCallback(() => onClick(drone), [onClick, drone]);

  if (isSelected) {
    return <ArcDroneMarker.idleSelected position={position} onClick={handleClick} lost={lost} />;
  }
  return <ArcDroneMarker.idleArrow position={position} onClick={handleClick} lost={lost} />;
});
IdleDroneRow.displayName = 'IdleDroneRow';

interface FlyingRowProps {
  drone: DroneEntity;
  rotation: number;
  isSelected: boolean;
  lost: boolean;
  onClick: (drone: DroneEntity) => void;
}

const FlyingDroneRow = React.memo<FlyingRowProps>(({ drone, rotation, isSelected, lost, onClick }) => {
  const position = React.useMemo(
    () => ({ lat: Number(drone.latitude), lng: Number(drone.longitude) }),
    [drone.latitude, drone.longitude]
  );
  const handleClick = React.useCallback(() => onClick(drone), [onClick, drone]);

  if (isSelected) {
    return (
      <ArcDroneMarker.missionSelected
        position={position}
        onClick={handleClick}
        rotation={rotation}
        lost={lost}
      />
    );
  }
  return (
    <ArcDroneMarker.missionArrow
      position={position}
      onClick={handleClick}
      rotation={rotation}
      lost={lost}
    />
  );
});
FlyingDroneRow.displayName = 'FlyingDroneRow';

/**
 * Component to render list of drone markers (idle and flying drones)
 * Handles both idle and mission drone markers with rotation support
 */
export const DroneMarkersList: React.FC<DroneMarkersListProps> = React.memo(({
  idleDrones,
  flyingDrones,
  clickedDrone,
  hoveredDrone,
  rotations,
  onDroneClick,
}) => {
  const { gpsInfo, flightData } = useFlightProvider();
  const flightSerial = flightData?.serialNumber;

  return (
    <>
      {idleDrones.map((drone) => {
        const isSelected =
          drone.serialNumber === clickedDrone?.serialNumber ||
          drone.serialNumber === hoveredDrone?.serialNumber;
        const lost = isDroneSignalLost(drone, gpsInfo, flightSerial);
        return (
          <IdleDroneRow
            key={drone.id}
            drone={drone}
            isSelected={isSelected}
            lost={lost}
            onClick={onDroneClick}
          />
        );
      })}
      {flyingDrones.map((drone) => {
        const rotation = rotations[drone.serialNumber] ?? getFlightHeading(drone.serialNumber) ?? 0;
        const isClicked = drone.serialNumber === clickedDrone?.serialNumber;
        const isHovered = drone.serialNumber === hoveredDrone?.serialNumber;
        const lost = isDroneSignalLost(drone, gpsInfo, flightSerial);

        if (isClicked || isHovered) {
          return (
            <ArcDroneMarker.missionSelected
              key={drone.id}
              position={{ lat: Number(drone.latitude), lng: Number(drone.longitude) }}
              onClick={() => onDroneClick(drone)}
              rotation={rotation}
              lost={lost}
            />
          );
        }

        const isSelected =
          drone.serialNumber === clickedDrone?.serialNumber ||
          drone.serialNumber === hoveredDrone?.serialNumber;
        return (
          <FlyingDroneRow
            key={drone.id}
            drone={drone}
            rotation={rotation}
            isSelected={isSelected}
            lost={lost}
            onClick={onDroneClick}
          />
        );
      })}
    </>
  );
});

DroneMarkersList.displayName = 'DroneMarkersList';
